/**
 * Rebinding (docs/spec/04 §7): two keys per action, a key used once only. Binding a key that is in
 * use moves it; an action is never left without a key unless the player cleared it. Pure.
 */

import type { Action, Bindings } from '../../platform/options';
import { ACTIONS, DEFAULT_BINDINGS } from '../../platform/options';
import { isBrowserKey } from '../kit/input';

/** The order the screen lists them: the game's own first, then the menus'. */
export const ACTION_ORDER: readonly Action[] = ['left', 'right', 'fire', 'bomb', 'pause', 'up', 'down'];

export const ACTION_LABELS: Readonly<Record<Action, string>> = {
  left: 'LEFT',
  right: 'RIGHT',
  fire: 'FIRE',
  bomb: 'NOVA',
  pause: 'PAUSE',
  up: 'MENU ▲',
  down: 'MENU ▼',
};

/** The same, for a screen reader. */
export const ACTION_WORDS: Readonly<Record<Action, string>> = {
  left: 'Move left',
  right: 'Move right',
  fire: 'Fire',
  bomb: 'Nova bomb',
  pause: 'Pause',
  up: 'Menu up',
  down: 'Menu down',
};

export const TOUCH_VALUES = ['auto', 'on', 'off'] as const;

/** Keys the menus need for themselves: binding them would leave no way to confirm or clear. */
const RESERVED = ['Enter', 'NumpadEnter', 'Tab', 'Backspace', 'Delete'];

/** Whether `code` may be bound to `action` (Escape only to pause, where it lives by default). */
export function canBind(action: Action, code: string): boolean {
  if (!code || RESERVED.includes(code) || isBrowserKey(code)) return false;
  return code !== 'Escape' || action === 'pause';
}

type Slots = Record<Action, [string, string]>;

function copy(b: Bindings): Slots {
  const out = {} as Slots;
  for (const a of ACTIONS) out[a] = [b[a][0], b[a][1]];
  return out;
}

function empty(pair: readonly [string, string]): boolean {
  return pair[0] === '' && pair[1] === '';
}

/**
 * Put `code` in the action's slot. If another slot held it, the key moves: that slot is emptied -
 * or, when that would leave its action with no key (or it is this action's other slot), it takes
 * the key this slot held, so the two swap. Null when the key cannot move: it is another action's
 * only key and this slot has nothing to give in return.
 */
export function bindKey(b: Bindings, action: Action, slot: 0 | 1, code: string): Bindings | null {
  const out = copy(b);
  const displaced = out[action][slot];
  if (displaced === code) return b;
  out[action][slot] = code;
  for (const a of ACTIONS) {
    for (const s of [0, 1] as const) {
      if (out[a][s] !== code || (a === action && s === slot)) continue;
      out[a][s] = '';
      if (a !== action && !empty(out[a])) continue;
      if (a !== action && displaced === '') return null;
      out[a][s] = displaced;
    }
  }
  return out;
}

/** Empty a slot: the player's own choice, so it may leave the action with no key. */
export function clearKey(b: Bindings, action: Action, slot: 0 | 1): Bindings {
  const cur = b[action];
  return { ...b, [action]: slot === 0 ? ['', cur[1]] : [cur[0], ''] };
}

/** Actions left with no key at all (the screen warns about them). */
export function unbound(b: Bindings): Action[] {
  return ACTION_ORDER.filter((a) => empty(b[a]));
}

/** The action that holds `code`, if any. */
export function holderOf(b: Bindings, code: string): Action | null {
  return ACTIONS.find((a) => b[a].includes(code)) ?? null;
}

/** The keys as shipped. */
export function resetKeys(): Bindings {
  return DEFAULT_BINDINGS;
}
